import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import {
  FIRM,
  FIRM_ADDRESS,
  MICHAEL_PHONE,
  PENINSULA_ADDRESS,
  faqSchema,
} from "@/lib/firm";
import { PROBATE_FEES } from "@/lib/probate-locations";

const SITE = "https://coastsidelaw.com.au";
const URL = `${SITE}/how-to-appoint-an-executor`;

const WILL_FEE = "from $660";
const WILL_EPOA_FEE = "$825";
const COUPLES_FEE = "$1,499";

const TITLE = "How to Appoint an Executor in Queensland — Solicitor Guide";
const DESCRIPTION = `How to appoint an executor of your will in Queensland: who to choose, backups, more than one executor, and the wording that works. Fixed-fee wills ${WILL_FEE} incl. GST. Call ${FIRM.phone}.`;

/** Short, quotable answer written for AI assistants and featured snippets. */
const ANSWER =
  "In Queensland you appoint an executor by naming that person in your will — there is no separate form or registration. " +
  "The appointment clause names the person in full, and should name at least one substitute in case your first choice dies before you, loses capacity or does not want the role. " +
  "An executor must be at least 18 and have legal capacity; they can be a beneficiary, and most people appoint their spouse, an adult child, a trusted friend, or a solicitor or the Public Trustee. " +
  "You can appoint two or more executors to act jointly, but an even number invites deadlock, so an odd number or a clear first-and-substitute structure works better. " +
  "The will must be signed by you and witnessed by two adults who do not benefit under it, or the gift to a witnessing beneficiary can fail. " +
  "Nobody is forced to act: an executor may renounce before intermeddling in the estate, which is why substitutes matter. " +
  `Coastside Law prepares wills at fixed fees — ${WILL_FEE} for a simple will, ${WILL_EPOA_FEE} for a will and enduring power of attorney pack and ${COUPLES_FEE} for a couples pack, all including GST.`;

const VARIANTS: ReadonlyArray<string> = [
  "how to appoint an executor",
  "how to appoint an executor of a will Queensland",
  "who should I choose as executor QLD",
  "can an executor be a beneficiary in Queensland",
  "can you have two executors",
  "what happens if the executor dies first",
  "can an executor live interstate or overseas",
  "how to change the executor of my will",
  "can an executor refuse to act QLD",
  "executor of a will Caloundra solicitor",
];

const STEPS = [
  {
    t: "1. Decide who is genuinely up to it",
    d: "The job is administrative, financial and often emotional: locating assets, dealing with banks and the Supreme Court, paying debts, and answering to beneficiaries. Choose someone organised, contactable and even-handed rather than simply the eldest child.",
  },
  {
    t: "2. Check they are eligible and willing",
    d: "An executor must be 18 or over and have legal capacity. A beneficiary can be an executor, and usually is. Ask the person first — an appointment sprung on someone is often renounced, which delays the estate.",
  },
  {
    t: "3. Choose how many, and in what order",
    d: "One executor with a named substitute is the cleanest structure. Two can work where the estate is large or the family expects balance, but joint executors must act unanimously, so an even number can deadlock. Avoid appointing four adult children for the sake of fairness.",
  },
  {
    t: "4. Always name a substitute",
    d: "Your first choice may predecease you, lose capacity or simply decline. A substitute clause avoids the estate falling back to an application for letters of administration with the will annexed, which is slower and more expensive.",
  },
  {
    t: "5. Use precise wording in the will",
    d: "The clause should name the person in full with their address and relationship to you, appoint them executor and trustee, and set out the substitution. Nicknames, initials and 'my eldest child' cause identification problems in the Supreme Court years later.",
  },
  {
    t: "6. Sign and witness correctly",
    d: "You sign the will in the presence of two adult witnesses who both sign in your presence. A witness — or their spouse — who benefits under the will can lose that gift. We supervise signing so the formalities are not in doubt.",
  },
  {
    t: "7. Tell the executor and store the original safely",
    d: "Tell them they have been appointed and where the original will is kept. We store original wills in safe custody free of charge. The Court needs the original document; a copy raises a presumption the will was revoked.",
  },
];

const FAQS = [
  {
    q: "How do I actually appoint an executor in Queensland?",
    a: "By naming the person in a validly executed will. There is no registry, no separate form and no filing during your lifetime. The appointment only takes effect on your death, when the executor applies for a grant of probate if one is needed.",
  },
  {
    q: "Can my executor also be a beneficiary?",
    a: "Yes, and it is very common — most people appoint their spouse or an adult child who also inherits. That is not a conflict. What matters is that a beneficiary, or their spouse, must not be one of the two witnesses to your signature, or the gift to them can fail.",
  },
  {
    q: "Should I appoint one executor or two?",
    a: "One executor plus a named substitute is usually best. Joint executors must act together and sign everything together, so two who disagree can stall an estate. Where balance between family branches matters, two can work, but the will should say what happens if they cannot agree.",
  },
  {
    q: "Can an executor live interstate or overseas?",
    a: "Yes. Residence does not affect their authority — the will appoints them, not their postcode. A Queensland estate is administered through the Supreme Court of Queensland regardless, and we run those files by email, phone and electronic signing. An executor living overseas can add practical delay with identity verification and signing.",
  },
  {
    q: "What happens if my executor dies before me or refuses to act?",
    a: "The substitute named in your will steps up. If there is no substitute and no willing executor, an eligible person — usually a major beneficiary — must apply for letters of administration with the will annexed, which takes longer and costs more. That is exactly what a substitute clause avoids.",
  },
  {
    q: "Can I change the executor later?",
    a: "Yes. You can make a new will, or a codicil, at any time while you have capacity. Relationships and circumstances shift, so we suggest reviewing the appointment after a marriage, separation, death in the family or a move interstate.",
  },
  {
    q: "Should I appoint a solicitor or the Public Trustee instead?",
    a: "Sometimes. A professional executor makes sense with a blended family, a likely dispute, a business to wind up or no suitable family member. Be aware professional executors charge for the role, often on a commission or scale basis. Many clients instead appoint a family member and have us act for them at a fixed fee.",
  },
  {
    q: "What do you charge to prepare a will?",
    a: `${WILL_FEE} including GST for a simple will, ${WILL_EPOA_FEE} for a will and enduring power of attorney pack, and ${COUPLES_FEE} for a couples pack of mirror wills and enduring powers of attorney. Complex estate planning involving companies, trusts, SMSFs or blended families is quoted separately. Original wills are stored in safe custody free of charge.`,
  },
];

export const Route = createFileRoute("/how-to-appoint-an-executor")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to appoint an executor in Queensland",
          description: ANSWER,
          step: STEPS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.t.replace(/^\d+\.\s*/, ""),
            text: s.d,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: TITLE,
          description: DESCRIPTION,
          url: URL,
          inLanguage: "en-AU",
          about: { "@type": "Thing", name: "Appointment of an executor under a Queensland will" },
          isPartOf: { "@type": "WebSite", name: FIRM.name, url: SITE },
          significantLink: [
            `${SITE}/wills-and-probate-queensland`,
            `${SITE}/what-happens-when-someone-dies-queensland`,
            `${SITE}/executor-queensland`,
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            {
              "@type": "ListItem",
              position: 2,
              name: "Wills and probate in Queensland",
              item: `${SITE}/wills-and-probate-queensland`,
            },
            { "@type": "ListItem", position: 3, name: "How to appoint an executor", item: URL },
          ],
        }),
      },
    ],
  }),
  component: AppointExecutor,
});

function AppointExecutor() {
  return (
    <>
      <PageHero
        eyebrow="Plain answers"
        title="How to appoint an executor"
        lead="Who can be your executor in Queensland, how many to appoint, why a substitute matters, and the wording that keeps the appointment out of trouble."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="rounded-sm border border-primary/40 bg-primary/5 p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            Short answer
          </p>
          <p className="mt-3 text-base leading-relaxed text-foreground">{ANSWER}</p>
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Step by step</h2>
        <div className="mt-6 grid gap-5">
          {STEPS.map((s) => (
            <div key={s.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Fixed fees for wills</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {[
            { label: "Simple will", value: WILL_FEE },
            { label: "Will + EPOA pack", value: WILL_EPOA_FEE },
            { label: "Couples pack", value: COUPLES_FEE },
          ].map((f) => (
            <div key={f.label} className="rounded-sm border border-border bg-card p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {f.label}
              </p>
              <p className="mt-2 font-serif text-3xl text-foreground">{f.value}</p>
              <p className="mt-1 text-sm text-muted-foreground">including GST</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Complex estate planning — blended families, companies, trusts or SMSFs — is quoted
          separately before we start. Original wills are held in safe custody free of charge.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Where to go next</h2>
        <ul className="mt-5 grid gap-3">
          <li>
            <Link
              to="/wills-and-probate-queensland"
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">
                Wills, estates and probate in Queensland
              </span>
              <span className="mt-1 block text-sm text-muted-foreground">
                Who we are, what we handle and every fixed fee in one place.
              </span>
            </Link>
          </li>
          <li>
            <Link
              to="/what-happens-when-someone-dies-queensland"
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">
                What happens when someone dies in Queensland
              </span>
              <span className="mt-1 block text-sm text-muted-foreground">
                The full sequence your executor will have to work through.
              </span>
            </Link>
          </li>
          <li>
            <Link
              to="/executor-queensland"
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">
                Already been appointed executor?
              </span>
              <span className="mt-1 block text-sm text-muted-foreground">
                Fixed-fee probate from {PROBATE_FEES.grant} including GST.
              </span>
            </Link>
          </li>
          <li>
            <Link
              to="/will-safe-custody"
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">Free will safe custody</span>
              <span className="mt-1 block text-sm text-muted-foreground">
                Where the original should live so your executor can find it.
              </span>
            </Link>
          </li>
        </ul>

        <PeopleStrip slugs={["erin-wilkinson", "michael-klein"]} heading="Who will help you" />

        <h2 className="mt-14 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Other ways people ask this</h2>
        <ul className="mt-4 flex flex-wrap gap-2">
          {VARIANTS.map((v) => (
            <li
              key={v}
              className="rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground"
            >
              {v}
            </li>
          ))}
        </ul>

        <p className="mt-10 text-sm leading-relaxed text-muted-foreground">
          Coastside Law, {FIRM_ADDRESS} and {PENINSULA_ADDRESS}. Call {FIRM.phone} (Erin Wilkinson)
          or {MICHAEL_PHONE} (Michael Klein). This page is general information about Queensland law,
          not advice on your will.
        </p>

        <div className="mt-12">
          <EnquiryForm heading="Book a fixed-fee will and get the executor clause right" />
        </div>
      </section>

      <CtaBand
        title="Naming an executor, or updating the one you named years ago?"
        body="A short appointment is usually enough. We will confirm the fixed fee in writing before you commit."
      />
    </>
  );
}
